import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, BarChart3, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import DemoBanner from "./DemoBanner";

const ENTRIES = [
  {
    key: "employee",
    icon: Users,
    title: "Employee experience",
    description:
      "Walk a BALM employee through choosing Call, Text, Video, or a confidential Message with a CALLACHAP chaplain.",
    to: "/care-portal/demo/balm/employee",
    cta: "Open employee demo",
  },
  {
    key: "employer",
    icon: BarChart3,
    title: "Employer administrator experience",
    description:
      "Show what a BALM administrator sees — aggregate utilization, privacy thresholds, sample CSV, and printable summary.",
    to: "/care-portal/demo/balm/employer",
    cta: "Open employer dashboard demo",
  },
];

export default function BalmDemoHub() {
  useEffect(() => {
    document.title = "BALM — Care Portal Demonstration";
  }, []);

  return (
    <main data-testid="balm-demo-hub" className="bg-white text-slate-900 min-h-screen">
      <DemoBanner subtitle="Choose the experience you want to show." />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <Link
          to="/care-portal/demo"
          data-testid="balm-hub-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Back to Demo Environments
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <img src="/callachap-logo.png" alt="CALLACHAP" className="h-10 md:h-12 w-auto" />
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">
          BALM — Demonstration Company
        </p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
          Two experiences. One private care service.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
          The BALM demonstration reuses the same CALLACHAP experience your prospective employees and
          employer administrators would receive through the CCCHAPS Care Portal.
        </p>

        <div className="mt-8 rounded-xl p-4 md:p-5 bg-[#F0F4F8] border border-[#4A6FA5]/15 flex items-start gap-3 text-sm text-[#3B597B]">
          <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.2} aria-hidden="true" />
          <p className="leading-relaxed">
            All BALM demonstration activity is isolated from live Care Portal data. No employee,
            employer, support request, admin, or Care Portal record is created, read, or modified.
          </p>
        </div>

        <div
          data-testid="balm-demo-hub-entries"
          className="mt-10 grid md:grid-cols-2 gap-5"
        >
          {ENTRIES.map((e) => (
            <Link
              key={e.key}
              to={e.to}
              data-testid={`balm-demo-hub-${e.key}`}
              className="group rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:border-[#4A6FA5]/60 transition-all"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors mb-4">
                <e.icon className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
              </span>
              <h2 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">
                {e.title}
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
                {e.description}
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A6FA5] group-hover:text-[#3B597B]">
                {e.cta} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
